import { useState, useCallback, useEffect } from 'react';

import { AnswerOption } from './AnswerOption';

import type { AnswerOptionsListProps, AnswerOptionConfig } from './types';

import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';
import { showErrorToast } from '@/plugins/Toast';
import { getLocalizedText } from '@/utils/localization';

export const AnswerOptionsList = ({
    options,
    selectedValues: controlledSelectedValues,
    onChange,
    maxSelection,
    multiple = false,
    className = '',
    dataTestId,
}: AnswerOptionsListProps) => {
    const { t } = useAmelioTranslations();
    const isRtl = i18n.language.startsWith('ar');
    const [internalSelectedValues, setInternalSelectedValues] = useState<string[]>([]);

    const isControlled = controlledSelectedValues !== undefined;
    const selectedValues = isControlled ? controlledSelectedValues : internalSelectedValues;

    const handleOptionClick = useCallback(
        (value: string) => {
            let newSelectedValues: string[];

            if (multiple) {
                if (selectedValues.includes(value)) {
                    newSelectedValues = selectedValues.filter((v) => v !== value);
                } else {
                    if (maxSelection !== undefined && selectedValues.length >= maxSelection) {
                        return;
                    }

                    newSelectedValues = [...selectedValues, value];
                }
            } else {
                newSelectedValues = selectedValues.includes(value) ? [] : [value];
            }

            if (!isControlled) {
                setInternalSelectedValues(newSelectedValues);
            }

            onChange(newSelectedValues);
        },
        [selectedValues, multiple, maxSelection, onChange, isControlled],
    );

    const isOptionDisabled = useCallback(
        (option: AnswerOptionConfig): boolean => {
            if (option.disabled) {
                return true;
            }

            if (
                multiple &&
                maxSelection !== undefined &&
                selectedValues.length >= maxSelection &&
                !selectedValues.includes(option.value)
            ) {
                return true;
            }

            return false;
        },
        [multiple, maxSelection, selectedValues],
    );

    useEffect(() => {
        if (controlledSelectedValues !== undefined) {
            setInternalSelectedValues(controlledSelectedValues);
        }
    }, [controlledSelectedValues]);

    return (
        <div
            className={`flex flex-col gap-3 ${className}`}
            role={multiple ? 'group' : 'radiogroup'}
            aria-label={'Answer options'}
            data-testid={dataTestId}
        >
            {options.map((option) => (
                <AnswerOption
                    key={option.value}
                    label={
                        typeof option.label === 'string'
                            ? option.label
                            : getLocalizedText(option.label)
                    }
                    value={option.value}
                    isSelected={selectedValues.includes(option.value)}
                    isRtl={isRtl}
                    onClick={() => {
                        if (isOptionDisabled(option)) {
                            showErrorToast({
                                message: t('answer.validation.maxChoicesReached'),
                            });
                            return;
                        }
                        handleOptionClick(option.value);
                    }}
                    dataTestId={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                />
            ))}
        </div>
    );
};
